import { shallowReactive } from "vue";
import type { Game, Item, Tile } from "..";
import type { itemId } from "../../data/types";
import { System, type EventHandlers } from "../../lib/core";
import type { ExplodeEffect } from "./shade";

export type DropPlan = {
  source: Tile;
  target: Tile;
  item: Item;
  cluster: Tile[];
  merge: { consume: Item[]; spawn: { itemId: itemId; tile: Tile }[] } | null;
  explode: ExplodeEffect | null;
  spawn: { itemId: itemId; tile: Tile }[] | null;
  swap: Item | null;
};

export type highlightMode = "drag" | "inspect";

type HighlightMap = Game["state"]["highlight"];

/** Semantic intermediate — what kind of highlight a plan triggers. Flattened to HighlightMap at the boundary. */
export type Highlight =
  | { kind: "idle"; source: Tile }
  | {
      kind: "explosion";
      explode: ExplodeEffect;
      target: Tile;
      source: Tile;
      mode: highlightMode;
      blocked: boolean;
    }
  | {
      kind: "merge";
      cluster: Tile[];
      target: Tile;
      source: Tile;
      swap: Item | null;
      mode: highlightMode;
    }
  | { kind: "spawn"; spawn: { itemId: itemId; tile: Tile }[]; target: Tile }
  | { kind: "match"; cluster: Tile[]; target: Tile; source: Tile; mode: highlightMode }
  | { kind: "landing"; target: Tile; source: Tile; mode: highlightMode };

export type UIEvents = {
  drop: { from: Tile | null; to: Tile | null; item: Item | null };
  hover: { reason: "tick" | "move" | "leave"; tile: Tile | null };
  dragStart: { from: Tile };
  dragOver: { over: Tile | null; item: Item | null };
  tap: { tile: Tile };
};

export class UISystem
  extends System<Game>()<UIEvents>(["drop", "hover", "dragStart", "dragOver", "tap"])
  implements EventHandlers<UIEvents>
{
  /**
   * Ephemeral UI state — not part of `game.state` because it's pointer-derived
   * and shouldn't be saved. shallowReactive: nested Tile/Item references
   * already live in the saved-state reactive graph; deep proxying would
   * double-wrap them.
   */
  state = shallowReactive<{
    activePlan: DropPlan | null;
    hovered: Tile | null;
    dragFrom: Tile | null;
    dragOver: Tile | null;
  }>({
    activePlan: null,
    hovered: null,
    dragFrom: null,
    dragOver: null,
  });

  drop({ from, to, item }: { from: Tile | null; to: Tile | null; item: Item | null }) {
    this.state.dragFrom = null;
    this.state.dragOver = null;
    this.state.activePlan = null;
    this.game.state.highlight = {};
    this.game.state.dragging = null;
    if (!from || !to || !item) return;
    if (from === to) return;
    const plan = this.planDrop(item, to);
    this.commitDrop(plan);
  }

  hover({ tile }: { reason: "tick" | "move" | "leave"; tile: Tile | null }) {
    const game = this.game;
    // Track hovered even while dragging so drop -> refreshActivePlan reads
    // the current pointer tile rather than the pre-drag one.
    this.state.hovered = tile;
    if (this.state.dragFrom) return; // drag handlers own plan + highlight
    if (!tile) {
      game.state.highlight = {};
      this.state.activePlan = null;
      return;
    }
    if (!tile.item) {
      game.state.highlight = { [tile.key]: { tile: "hover", item: null } };
      this.state.activePlan = null;
      return;
    }
    if (tile.shade > 0) {
      // No useful inspect for shaded items; clear plan but leave highlight.
      this.state.activePlan = null;
      return;
    }
    const plan = this.planDrop(tile.item, tile);
    this.state.activePlan = plan;
    game.state.highlight = this.highlightFor(plan, "inspect");
  }

  dragStart({ from }: { from: Tile }) {
    if (!this.canDrag(from)) return;
    this.state.dragFrom = from;
    this.state.dragOver = null;
    this.state.activePlan = null;
    this.game.state.dragging = { from: from.position, over: null, itemUid: from.item!.uid };
    this.game.state.highlight = { [from.key]: { tile: "hover", item: "hover" } };
  }

  dragOver({ over, item }: { over: Tile | null; item: Item | null }) {
    const game = this.game;
    if (!item) return;
    const sourceTile = game.grid.tryAt(item.position!);
    if (!sourceTile) return;
    this.state.dragOver = over;
    if (!this.canDrag(sourceTile)) {
      game.state.highlight = {};
      this.state.activePlan = null;
      return;
    }
    if (!over) {
      game.state.dragging = { from: sourceTile.position, over: null, itemUid: item.uid };
      game.state.highlight = { [sourceTile.key]: { tile: "hover", item: "hover" } };
      this.state.activePlan = null;
      return;
    }
    game.state.dragging = { from: sourceTile.position, over: over.position, itemUid: item.uid };
    const plan = this.planDrop(item, over);
    this.state.activePlan = plan;
    game.state.highlight = this.highlightFor(plan, "drag");
  }

  /**
   * Re-derive activePlan + highlight from current resolver state. Called from
   * the per-tick handler so charge changes propagate into the live plan and
   * preview without the renderer having to re-emit hover/dragOver.
   */
  refreshActivePlan() {
    if (this.state.dragFrom) {
      this.dragOver({
        over: this.state.dragOver,
        item: this.state.dragFrom.item ?? null,
      });
      return;
    }
    if (this.state.hovered) {
      this.hover({ tile: this.state.hovered, reason: "tick" });
    }
  }

  /** Whether the item on `tile` can be picked up: present, not shaded, not animating. */
  canDrag(tile: Tile): boolean {
    if (!tile.item) return false;
    if (tile.shade) return false;
    if (tile.item.animation) return false;
    return true;
  }

  tap({ tile }: { tile: Tile }) {
    const game = this.game;
    const item = tile.item;
    if (!item || item.animation) return;

    if (item.data.shade?.explodeOnShade) {
      game.shade.contact({ item, tile });
      return;
    }

    if (tile.shade > 0) return;

    const preview = game.item.previewTapSpawn(item);
    if (!preview) return;
    if (game.item.applyTapSpawn(preview)) game.merge.settleBoard();
  }

  /** Compose a DropPlan describing what `item` would do landing on `target`. */
  planDrop(item: Item, target: Tile): DropPlan {
    const game = this.game;
    const source = item.position ? game.grid.tryAt(item.position) ?? target : target;

    let explode: ExplodeEffect | null = null;
    const explodePreview = game.shade.previewExplosion(item, target);
    if (explodePreview && explodePreview.affected.some((a) => a.tile.shade > 0)) {
      explode = explodePreview;
    }

    const cluster = game.merge.clusterFor(item, target);

    let merge: DropPlan["merge"] = null;
    const result = game.merge.calculateDropMerge(target, item, "drop", "auto")
      ?? game.merge.calculateDropMerge(target, item, "drop", "manual");
    if (result?.spawn) {
      merge = { consume: result.connectedItems, spawn: result.spawn };
    }

    const swap = target !== source && target.item && (!merge || target.item.id !== item.id)
      ? target.item
      : null;

    let spawn: { itemId: itemId; tile: Tile }[] | null = null;
    if (target === source) {
      const tapPreview = game.item.previewTapSpawn(item);
      if (tapPreview && tapPreview.spawn.length > 0) spawn = tapPreview.spawn;
    }

    return { source, target, item, cluster, merge, explode, spawn, swap };
  }

  /** Apply a drop plan: move/swap the item and trigger merge if any. Returns true on success. */
  commitDrop(plan: DropPlan): boolean {
    if (plan.source.key === plan.target.key) return false;
    if (plan.source.shade > 0) return false;
    if (plan.target.shade > 0) {
      if (!plan.item.data.shade?.explodeOnShade) return false;
      if (plan.target.item) return false;
    }
    const game = this.game;
    const sourcePosition = { ...plan.source.position };
    const targetPosition = { ...plan.target.position };

    plan.source.item = plan.swap ?? null;
    if (plan.swap) plan.swap.position = plan.source.position;
    plan.target.item = plan.item;
    plan.item.position = plan.target.position;

    if (plan.swap) {
      const duration = game.data.animations["item:swapMove"].duration;
      plan.swap.animation = {
        type: "item:swapMove",
        from: targetPosition,
        to: sourcePosition,
        startTick: game.state.tick,
        endTick: game.state.tick + duration,
      };
    }

    if (plan.merge) {
      game.merge.execute({ connectedItems: plan.merge.consume, spawn: plan.merge.spawn });
    } else {
      game.merge.checkAll("auto");
    }

    return true;
  }

  /** Pick the semantic highlight a plan triggers (priority order). */
  planHighlight(plan: DropPlan, mode: highlightMode): Highlight {
    return (
      this.highlightIdle(plan, mode) ??
      this.highlightExplosion(plan, mode) ??
      this.highlightMerge(plan, mode) ??
      this.highlightSpawn(plan, mode) ??
      this.highlightMatch(plan, mode) ??
      this.highlightLanding(plan, mode)
    );
  }

  /** Plan → renderer-shaped highlight map. */
  highlightFor(plan: DropPlan, mode: highlightMode): HighlightMap {
    return this.highlightToMap(this.planHighlight(plan, mode));
  }

  highlightIdle(plan: DropPlan, mode: highlightMode): Highlight | null {
    if (mode !== "drag") return null;
    if (plan.source.key === plan.target.key) {
      return { kind: "idle", source: plan.source };
    }
    if (
      plan.source.shade > 0 ||
      (plan.target.shade > 0 && !plan.item.data.shade?.explodeOnShade)
    ) {
      return { kind: "idle", source: plan.source };
    }
    return null;
  }

  highlightExplosion(plan: DropPlan, mode: highlightMode): Highlight | null {
    if (!plan.explode) return null;
    const showInspect =
      mode === "inspect" && plan.explode.affected.some((a) => a.tile.shade > 0);
    const showDrag = mode === "drag" && plan.target.shade > 0;
    if (!showInspect && !showDrag) return null;
    return {
      kind: "explosion",
      explode: plan.explode,
      target: plan.target,
      source: plan.source,
      mode,
      blocked: mode === "drag" && !!plan.target.item,
    };
  }

  highlightMerge(plan: DropPlan, mode: highlightMode): Highlight | null {
    if (!plan.merge) return null;
    return {
      kind: "merge",
      cluster: plan.cluster,
      target: plan.target,
      source: plan.source,
      swap: plan.swap,
      mode,
    };
  }

  highlightSpawn(plan: DropPlan, mode: highlightMode): Highlight | null {
    if (mode !== "inspect" || !plan.spawn) return null;
    return { kind: "spawn", spawn: plan.spawn, target: plan.target };
  }

  highlightMatch(plan: DropPlan, mode: highlightMode): Highlight | null {
    const matchTrigger =
      mode === "inspect"
        ? plan.cluster.length > 1
        : plan.cluster.some((t) => t.item?.id === plan.item.id);
    if (!matchTrigger) return null;
    return { kind: "match", cluster: plan.cluster, target: plan.target, source: plan.source, mode };
  }

  highlightLanding(plan: DropPlan, mode: highlightMode): Highlight {
    return { kind: "landing", target: plan.target, source: plan.source, mode };
  }

  /** Flatten a semantic highlight into the renderer-keyed map. */
  highlightToMap(h: Highlight): HighlightMap {
    switch (h.kind) {
      case "idle":
        return { [h.source.key]: { tile: "hover", item: "hover" } };
      case "explosion": {
        const map: HighlightMap = {};
        for (const a of h.explode.affected) {
          if (h.blocked) {
            map[a.tile.key] = { tile: "cover", item: null };
          } else if (a.damage > 0 && a.clears) {
            map[a.tile.key] = { tile: "cover-clear", item: null };
          } else if (a.damage > 0) {
            map[a.tile.key] = { tile: "cover-damage", item: null };
          } else {
            map[a.tile.key] = { tile: "cover", item: null };
          }
        }
        if (h.mode === "inspect") {
          map[h.target.key] = { tile: "hover", item: null };
        } else {
          map[h.source.key] = { tile: "hover", item: "hover" };
        }
        return map;
      }
      case "merge": {
        const map: HighlightMap = {};
        for (const tile of h.cluster) {
          map[tile.key] = { tile: "merge", item: tile.item ? "merge" : null };
        }
        if (h.swap) {
          map[h.target.key] = { item: "swap", tile: "merge" };
          map[h.source.key] = { item: "merge", tile: "swap" };
        } else if (h.mode === "drag") {
          map[h.source.key] = { item: "merge", tile: "hover" };
        }
        return map;
      }
      case "spawn": {
        const map: HighlightMap = {};
        for (const s of h.spawn) {
          map[s.tile.key] = { tile: "spawn", item: null };
        }
        map[h.target.key] = { tile: "hover", item: null };
        return map;
      }
      case "match": {
        const map: HighlightMap = {};
        for (const tile of h.cluster) {
          map[tile.key] =
            h.mode === "inspect"
              ? { tile: "match", item: null }
              : { tile: "match", item: tile.item ? "match" : null };
        }
        if (h.mode === "drag") map[h.source.key] = { item: "match", tile: "hover" };
        return map;
      }
      case "landing": {
        const map: HighlightMap = {};
        if (h.mode === "drag") {
          map[h.target.key] = { tile: "drop", item: null };
          if (h.source.key !== h.target.key) {
            map[h.source.key] = { tile: "hover", item: "hover" };
          }
        } else {
          map[h.target.key] = { tile: "hover", item: null };
        }
        return map;
      }
    }
  }
}
